// fineLinesData.ts

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string;
        type?: string;
        title?: string;
        description?: string;
        url?: string;
        site_name?: string;
        updated_time?: string;
        image?: string;
        image_secure_url?: string;
        image_width?: number;
        image_height?: number;
        image_alt?: string;
        image_type?: string;
        video?: string;
        video_duration?: number;
        video_upload_date?: string;
        video_allow_embed?: boolean;
    };
    twitter?: {
        card?: string;
        title?: string;
        description?: string;
        image?: string;
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    treatmentAreas1: TreatmentArea[];
    treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string;
        beforeAfter: string[] | any;
        introductionImage?: string;
    };
    product: {
        id: number;
        title: string;
        price: number;
        description: string;
    };
}

export const fineLines: {
    en: LanguageContent;
    ar: LanguageContent;
    shared: SharedData;
} =
{
    "en": {
        "url": "",
        "meta": {
            "title": "Best Fine Lines Treatment Dubai (FREE Consultation) | Elite Body Home",
            "description": "Professional fine lines treatment in Dubai with FREE consultation. Our experts provide safe & effective solutions for early signs of aging. Book your appointment today!",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/services/fine-lines-treatment/",
            "og": {
                "locale": "en_US",
                "type": "article",
                "title": "Best Fine Lines Treatment Dubai (FREE Consultation) | Elite Body Home",
                "description": "Professional fine lines treatment in Dubai with FREE consultation. Our experts provide safe & effective solutions for early signs of aging. Book your appointment today!",
                "url": "https://elitebodyhome.com/services/fine-lines-treatment/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2025-01-22T13:54:57+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Fine-Lines-Treatment.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Fine-Lines-Treatment.jpg",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "Fine Lines Treatment Dubai",
                "image_type": "image/jpeg"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "Best Fine Lines Treatment Dubai (FREE Consultation) | Elite Body Home",
                "description": "Professional fine lines treatment in Dubai with FREE consultation. Our experts provide safe & effective solutions for early signs of aging. Book your appointment today!",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Fine-Lines-Treatment.jpg"
            }
        },
        "breadcrumbs": ["Home", "Services", "Fine Lines Treatment"],
        "hero": {
            "title": "Fine Lines Treatment",
            "subtitle": "",
            "description": "Fine lines are one of the earliest and most common signs of aging that can appear on the face. These small, shallow wrinkles often begin to form around the eyes, mouth, and forehead as the skin starts losing its elasticity. While natural aging is a primary cause, environmental factors, lifestyle choices, and genetics also play a significant role. Fortunately, there are numerous treatments available that help reduce the appearance of fine lines and prevent them from deepening into wrinkles."
        },
        "howItWorks": {
            "title": "What Are Fine Lines?",
            "description": "Fine lines are shallow, small wrinkles that typically appear in areas where facial expressions are most frequent, such as the forehead, around the eyes (often referred to as 'crow's feet'), and the mouth. Over time, as the skin's collagen production decreases, fine lines can develop into deeper wrinkles. Factors like UV exposure, dehydration, and lifestyle habits such as smoking can exacerbate their development."
        },
        "procedureSteps": {
            "heading": "Procedure for Fine Line Treatment",
            "para": "When it comes to treating fine lines, there are a variety of effective procedures to choose from, depending on the severity of the lines and individual skin needs. Professional treatments offered at clinics can be both non-invasive and minimally invasive, targeting fine lines to rejuvenate the skin and restore a youthful appearance.",
            "steps": [
                {
                    "title": "Consultation and Skin Analysis",
                    "desc": "A thorough assessment of your skin to determine the best treatment approach for fine lines, with personalized recommendations based on your skin type and goals.",
                    "icon": "fa-solid fa-user-doctor"
                },
                {
                    "title": "Preparation",
                    "desc": "The treatment area is cleansed, and a numbing cream may be applied to ensure comfort during the procedure.",
                    "icon": "fa-solid fa-bubbles"
                },
                {
                    "title": "Microneedling",
                    "desc": "Tiny needles stimulate collagen production to reduce fine lines.",
                    "icon": "fa-solid fa-needle"
                },
                {
                    "title": "Chemical Peels",
                    "desc": "Exfoliates the skin's surface, promoting new cell growth for smoother skin.",
                    "icon": "fa-solid fa-prescription-bottle"
                },
                {
                    "title": "Laser Resurfacing",
                    "desc": "Uses laser technology to promote collagen and elastin production, reducing fine lines.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "Injectables",
                    "desc": "Botox or fillers target specific areas to smooth fine lines and restore youthful skin.",
                    "icon": "fa-solid fa-syringe"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits",
            "subheading": "",
            "para": "Treating fine lines offers more than just aesthetic improvements; it can boost confidence and improve skin health. These treatments help delay further aging and reduce the visibility of fine lines that deepen with time.",
            "items": [
                {
                    "title": "Youthful appearance",
                    "desc": "Smooths fine lines to maintain a youthful and fresh appearance.",
                    "icon": "fa-solid fa-face-smile"
                },
                {
                    "title": "Collagen stimulation",
                    "desc": "Stimulates collagen production, which makes your skin look firmer and younger overall.",
                    "icon": "fa-solid fa-arrow-up"
                },
                {
                    "title": "Prevent deeper wrinkles",
                    "desc": "Taking care of fine lines can help to stop them from becoming deep wrinkles.",
                    "icon": "fa-solid fa-shield"
                },
                {
                    "title": "Increased confidence",
                    "desc": "A more youthful look can enhance self-esteem and confidence.",
                    "icon": "fa-solid fa-heart"
                },
                {
                    "title": "Healthier skin",
                    "desc": "Professional treatments help improve the health of the skin by increasing cell turnover and hydration.",
                    "icon": "fa-solid fa-spa"
                }
            ]
        },
        "eligibility": {
            "heading": "Eligibility",
            "subheading": "",
            "para": "Not everyone is a candidate for every treatment, but the majority of people with visible fine lines can be treated to some degree. It is essential to analyze your skin tone and general health in order to find whatever method is right for you.",
            "items": [
                {
                    "title": "Visible fine lines",
                    "desc": "Great for those starting to see early signs of aging.",
                    "icon": "fa-solid fa-face-smile-wink"
                },
                {
                    "title": "Good overall health",
                    "desc": "Candidates must be healthy and not have skin conditions that would prevent proper healing.",
                    "icon": "fa-solid fa-heart-pulse"
                },
                {
                    "title": "Realistic expectations",
                    "desc": "It is necessary to know what the treatment can do.",
                    "icon": "fa-solid fa-clipboard-check"
                },
                {
                    "title": "UV protection",
                    "desc": "Treatment works best when patients consistently shield their skin from the sun.",
                    "icon": "fa-solid fa-sun"
                }
            ]
        },
        "preparationTips": {
            "heading": "Preparation",
            "subheading": "",
            "para": "Getting ready for fine line treatments. The magic is in the preparation when it comes to fine line treatments. Your skin will need to be prepped depending on which type of procedure you opt for.",
            "items": [
                {
                    "title": "Consult a specialist",
                    "desc": "If you're in doubt about which method is best for you, discuss your skin concerns and what to expect with a professional.",
                    "icon": "fa-solid fa-notes-medical"
                },
                {
                    "title": "Avoid sun exposure",
                    "desc": "The presence of sunburn or too much tan can interfere with the performance of the system.",
                    "icon": "fa-solid fa-sun"
                },
                {
                    "title": "Hydrate and moisturize",
                    "desc": "Before the procedure, make sure your skin is well moisturized to facilitate healing and increase the effectiveness of treatments.",
                    "icon": "fa-solid fa-bottle-water"
                },
                {
                    "title": "Stop smoking",
                    "desc": "Quitting smoking or lowering your intake before treatment will help your body to heal and will also maximize the results.",
                    "icon": "fa-solid fa-smoking-ban"
                }
            ]
        },
        "results": {
            "heading": "Results",
            "subheading": "",
            "para": "After fine line treatment, the skin will feel smoother, tighter and the appearance of fine lines will be diminished. Results are procedure-dependent, but are typically quite satisfying.",
            "items": [
                {
                    "title": "Smoother skin texture",
                    "desc": "Fine wrinkles are less noticeable, giving you smoother skin.",
                    "icon": "fa-solid fa-hand-sparkles"
                },
                {
                    "title": "Increased skin firmness",
                    "desc": "The production of collagen results in a tighter, firmer appearance of the skin.",
                    "icon": "fa-solid fa-arrow-up"
                },
                {
                    "title": "Natural-looking rejuvenation",
                    "desc": "The results give a rejuvenated look without looking 'overdone'.",
                    "icon": "fa-solid fa-face-laugh-beam"
                }
            ]
        },
        "aftercareTips": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": []
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "",
            "items": [
                {
                    "question": "How soon will I see results after fine lines treatment?",
                    "answer": "Results vary depending on the treatment method. Some treatments like Botox show effects in 3-7 days, while collagen-stimulating treatments like microneedling show gradual improvement over several weeks as new collagen forms."
                },
                {
                    "question": "Are fine line treatments painful?",
                    "answer": "Most treatments involve minimal discomfort. Injections may cause slight stinging, while laser and microneedling feel like mild pricking. Topical anesthetics are used to ensure your comfort throughout the procedure."
                },
                {
                    "question": "How long do the results last?",
                    "answer": "Results typically last 3-6 months for injectables like Botox, 6-18 months for fillers, and several years for collagen-stimulating treatments like laser resurfacing, though maintenance sessions are recommended."
                },
                {
                    "question": "Can fine lines be completely removed?",
                    "answer": "While treatments can significantly reduce the appearance of fine lines, complete removal depends on their depth and your skin's condition. Early treatment yields the best results in preventing them from becoming deeper wrinkles."
                },
                {
                    "question": "Is there any downtime after treatment?",
                    "answer": "Most fine line treatments require minimal to no downtime. You may experience slight redness or swelling that typically fades within hours to a couple of days."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    },
    "shared": {
        "images": {
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Fine-Lines-treatement-in-dubai-uae.png",
            "beforeAfter": [
                { "src": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Fine-Lines-treatement-in-dubai-1.jpg", "alt": "fine lines treatment before after" },
                { "src": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Fine-Lines-treatement-in-dubai-2.jpg", "alt": "fine lines reduction results" },
                { "src": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Fine-Lines-treatement-in-dubai-3.jpg", "alt": "anti-aging treatment results" }
            ]
        },
        "product": {
            "id": 319,
            "title": "Fine Lines Treatment Package",
            "price": 799,
            "description": "Comprehensive fine lines treatment package including consultation, treatment sessions, and follow-up care. Our expert team offers effective solutions tailored to your skin needs."
        }
    },
    "ar": {
        "url": "",
        "meta": {
            "title": "أفضل علاج للخطوط الدقيقة في دبي (استشارة مجانية) | ايليت بودي هوم",
            "description": "علاج احترافي للخطوط الدقيقة في دبي مع استشارة مجانية. يقدم خبراؤنا حلولاً آمنة وفعالة للعلامات المبكرة للتقدم في العمر. احجز موعدك اليوم!",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/ar/services/fine-lines-treatment/",
            "og": {
                "locale": "ar_AR",
                "type": "article",
                "title": "أفضل علاج للخطوط الدقيقة في دبي (استشارة مجانية) | ايليت بودي هوم",
                "description": "علاج احترافي للخطوط الدقيقة في دبي مع استشارة مجانية. يقدم خبراؤنا حلولاً آمنة وفعالة للعلامات المبكرة للتقدم في العمر. احجز موعدك اليوم!",
                "url": "https://elitebodyhome.com/ar/services/fine-lines-treatment/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2025-01-22T13:54:57+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Fine-Lines-Treatment.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Fine-Lines-Treatment.jpg",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "علاج الخطوط الدقيقة دبي",
                "image_type": "image/jpeg"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "أفضل علاج للخطوط الدقيقة في دبي (استشارة مجانية) | ايليت بودي هوم",
                "description": "علاج احترافي للخطوط الدقيقة في دبي مع استشارة مجانية. يقدم خبراؤنا حلولاً آمنة وفعالة للعلامات المبكرة للتقدم في العمر. احجز موعدك اليوم!",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Fine-Lines-Treatment.jpg"
            }
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "علاج الخطوط الدقيقة"],
        "hero": {
            "title": "علاج الخطوط الدقيقة",
            "subtitle": "",
            "description": "تُعد الخطوط الدقيقة من أول علامات شيخوخة البشرة وأكثرها شيوعًا، وغالبًا ما تظهر في مناطق مثل محيط العينين والفم والجبهة، مع بدء فقدان الجلد لمرونته. ورغم أن التقدم الطبيعي في العمر هو السبب الأساسي، إلا أن العوامل البيئية، ونمط الحياة، والعوامل الوراثية تلعب دورًا كبيرًا أيضًا. لحسن الحظ، يوجد العديد من طرق العلاج المتوفرة التي تساعد في تقليل مظهر الخطوط الدقيقة ومنع تطورها إلى تجاعيد أعمق."
        },
        "howItWorks": {
            "title": "ما هي الخطوط الدقيقة؟",
            "description": "الخطوط الدقيقة هي تجاعيد سطحية وصغيرة تظهر عادةً في المناطق التي تتكرر فيها تعابير الوجه، مثل الجبهة، وحول الفم، وحول العينين التي تُعرف بتجاعيد أقدام الغراب. وقد تتحول هذه الخطوط إلى تجاعيد أعمق مع مرور الوقت وانخفاض إنتاج الكولاجين في البشرة. كما يمكن لبعض العوامل مثل التعرض للأشعة فوق البنفسجية، والجفاف، والعادات اليومية مثل التدخين أن تسرع من ظهورها وتفاقمها."
        },
        "procedureSteps": {
            "heading": "خطوات علاج الخطوط الدقيقة",
            "para": "تتوفر العديد من الإجراءات الفعالة لعلاج الخطوط الدقيقة، ويحدد نوع العلاج بناءً شدة الخطوط واحتياجات بشرة كل شخص. تشمل الطرق الطبية للعلاج المقدمة في العيادات خيارات غير جراحية وأخرى طفيفة التوغل، تعمل على استهداف الخطوط الدقيقة وتجديد البشرة لمنحها مظهرًا أكثر شبابًا.",
            "steps": [
                {
                    "title": "الاستشارة وتحليل البشرة",
                    "desc": "يبدأ العلاج بتقييم شامل لحالة بشرتك لتحديد النهج الأنسب لعلاج الخطوط الدقيقة، مع تقديم توصيات مخصصة حسب نوع بشرتك وأهدافك.",
                    "icon": "fa-solid fa-user-doctor"
                },
                {
                    "title": "الاستعداد",
                    "desc": "تُنظف منطقة العلاج جيدًا، وقد يُستخدم مخدر موضعي لضمان راحتك أثناء الإجراء.",
                    "icon": "fa-solid fa-bubbles"
                },
                {
                    "title": "الميكرونيديلنغ",
                    "desc": "يعمل على تقليل الخطوط الدقيقة من خلال تحفيز إنتاج الكولاجين باستخدام إبر دقيقة.",
                    "icon": "fa-solid fa-needle"
                },
                {
                    "title": "التقشير الكيميائي",
                    "desc": "يساعد التقشير الكيميائي على تقشير الطبقة السطحية من الجلد لتجديد الخلايا وتحسين ملمس البشرة.",
                    "icon": "fa-solid fa-prescription-bottle"
                },
                {
                    "title": "تجديد سطح البشرة بالليزر",
                    "desc": "تُستخدم تقنية الليزر لتحفيز إنتاج الكولاجين والإيلاستين، مما يقلل من مظهر الخطوط الدقيقة.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "الإبر التجميلية",
                    "desc": "تستهدف مناطق محددة لتنعيم الخطوط الدقيقة واستعادة شباب البشرة.",
                    "icon": "fa-solid fa-syringe"
                }
            ]
        },
        "benefits": {
            "heading": "الفوائد",
            "subheading": "",
            "para": "لا يقتصر علاج الخطوط الدقيقة على تحسين المظهر الجمالي فقط، بل يساهم في تعزيز الثقة بالنفس وتحسين صحة البشرة بشكل عام، كما يساعد العلاج على إبطاء علامات التقدم في العمر ومنع تفاقم التجاعيد مع مرور الوقت.",
            "items": [
                {
                    "title": "مظهر أكثر شبابًا",
                    "desc": "يساعد على تنعيم الخطوط الدقيقة للحفاظ على مظهر نضر وشاب.",
                    "icon": "fa-solid fa-face-smile"
                },
                {
                    "title": "تحفيز إنتاج الكولاجين",
                    "desc": "يعمل العلاج على تحفيز الكولاجين، مما يمنح البشرة مظهرًا أكثر تماسكًا وحيوية.",
                    "icon": "fa-solid fa-arrow-up"
                },
                {
                    "title": "الوقاية من التجاعيد العميقة",
                    "desc": "يقلل الاهتمام المبكر بالخطوط الدقيقة من فرص تطورها إلى تجاعيد عميقة.",
                    "icon": "fa-solid fa-shield"
                },
                {
                    "title": "تعزيز الثقة بالنفس",
                    "desc": "يعزز المظهر الشاب والمنعش من الشعور بالرضا والثقة في النفس.",
                    "icon": "fa-solid fa-heart"
                },
                {
                    "title": "بشرة أكثر صحة",
                    "desc": "يساهم العلاج التجميلي الاحترافي في تجديد خلايا البشرة وتحسين ترطيبها.",
                    "icon": "fa-solid fa-spa"
                }
            ]
        },
        "eligibility": {
            "heading": "الأهلية",
            "subheading": "",
            "para": "تختلف ملاءمة العلاج من شخص لآخر، لكن يمكن علاج معظم الأشخاص الذين يعانون من خطوط دقيقة ظاهرة لدرجة ما. يجب تقييم درجة لون البشرة والحالة الصحية العامة لتحديد العلاج الأنسب.",
            "items": [
                {
                    "title": "وجود خطوط دقيقة ظاهرة",
                    "desc": "يُعد علاج مثالي للأشخاص الذين بدأوا بملاحظة علامات مبكرة للتقدم في العمر.",
                    "icon": "fa-solid fa-face-smile-wink"
                },
                {
                    "title": "صحة عامة جيدة",
                    "desc": "ينبغي أن يكون المريض بصحة جيدة ولا يعاني من أمراض جلدية تعيق عملية التعافي.",
                    "icon": "fa-solid fa-heart-pulse"
                },
                {
                    "title": "توقعات واقعية",
                    "desc": "يجب فهم ما يمكن أن يقدمه العلاج.",
                    "icon": "fa-solid fa-clipboard-check"
                },
                {
                    "title": "الالتزام بالحماية من الشمس",
                    "desc": "يحقق العلاج أفضل النتائج عند الالتزام الدائم باستخدام واقي الشمس لحماية البشرة.",
                    "icon": "fa-solid fa-sun"
                }
            ]
        },
        "preparationTips": {
            "heading": "الاستعداد",
            "subheading": "",
            "para": "يُعد التحضير الجيد هو السر الحقيقي لنجاح علاج الخطوط الدقيقة. تعتمد طريقة تجهيز بشرتك على نوع الإجراء الذي ستخضع له. إليك بعض الخطوات المهمة التي يجب اتباعها قبل العلاج:",
            "items": [
                {
                    "title": "استشارة طبيب متخصص",
                    "desc": "إذا لم تكن متأكدًا من العلاج الأنسب لك، ناقش مخاوفك الجلدية وتوقعاتك مع طبيب متخصص لتحديد الخيار الأفضل لحالتك.",
                    "icon": "fa-solid fa-notes-medical"
                },
                {
                    "title": "تجنب التعرض للشمس",
                    "desc": "قد تؤثر حروق الشمس سلبًا على فاعلية العلاج.",
                    "icon": "fa-solid fa-sun"
                },
                {
                    "title": "الترطيب الجيد للبشرة",
                    "desc": "احرص على ترطيب بشرتك بانتظام قبل العلاج، حيث يساعد الترطيب في تسريع عملية الشفاء ويعزز نتائج العلاج.",
                    "icon": "fa-solid fa-bottle-water"
                },
                {
                    "title": "الامتناع عن التدخين",
                    "desc": "يساعد التوقف عن التدخين أو تقليله قبل الجلسة على الشفاء بشكل أفضل ويرفع من فاعلية العلاج ويحافظ على النتائج لفترة أطول.",
                    "icon": "fa-solid fa-smoking-ban"
                }
            ]
        },
        "results": {
            "heading": "النتائج",
            "subheading": "",
            "para": "ستلاحظ تحسنًا ملحوظًا في ملمس البشرة ومظهرها العام بعد الخضوع لعلاج الخطوط الدقيقة. قد تختلف النتائج حسب نوع الإجراء المستخدم، لكنها غالبًا ما تكون مرضية بشكل كبير.",
            "items": [
                {
                    "title": "ملمس أكثر نعومة",
                    "desc": "تقل ملاحظة التجاعيد الدقيقة، مما يمنحك مظهرًا ناعمًا ومشرقًا.",
                    "icon": "fa-solid fa-hand-sparkles"
                },
                {
                    "title": "زيادة تماسك البشرة",
                    "desc": "يساهم تحفيز إنتاج الكولاجين في شد البشرة ومنحها مظهرًا أكثر تماسكًا وشبابًا.",
                    "icon": "fa-solid fa-arrow-up"
                },
                {
                    "title": "تجديد طبيعي للمظهر",
                    "desc": "تمنحك النتائج مظهرًا متجددًا وحيويًا دون أن يبدو مبالغًا فيه.",
                    "icon": "fa-solid fa-face-laugh-beam"
                }
            ]
        },
        "aftercareTips": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": []
        },
        "faqData": {
            "heading": "الأسئلة الشائعة",
            "subheading": "",
            "items": [
                {
                    "question": "متى سأرى نتائج علاج الخطوط الدقيقة؟",
                    "answer": "تختلف النتائج حسب طريقة العلاج. تظهر بعض العلاجات مثل البوتوكس تأثيرها خلال 3-7 أيام، بينما تظهر العلاجات المحفزة للكولاجين مثل الميكرونيدلينغ تحسنًا تدريجيًا على مدار عدة أسابيع مع تكوّن كولاجين جديد."
                },
                {
                    "question": "هل علاج الخطوط الدقيقة مؤلم؟",
                    "answer": "معظم العلاجات تتضمن انزعاجًا بسيطًا. قد تسبب الحقن لسعة خفيفة، بينما يشبه الليزر والميكرونيدلينغ وخزًا خفيفًا. تُستخدم مخدرات موضعية لضمان راحتك خلال الإجراء."
                },
                {
                    "question": "كم تدوم نتائج العلاج؟",
                    "answer": "تدوم النتائج عادةً من 3-6 أشهر للحقن مثل البوتوكس، و6-18 شهرًا للفيلر، وعدة سنوات للعلاجات المحفزة للكولاجين مثل تجديد البشرة بالليزر، رغم أن جلسات المتابعة موصى بها."
                },
                {
                    "question": "هل يمكن إزالة الخطوط الدقيقة تمامًا؟",
                    "answer": "بينما يمكن للعلاجات أن تقلل بشكل كبير من مظهر الخطوط الدقيقة، فإن الإزالة الكاملة تعتمد على عمقها وحالة بشرتك. يعطي العلاج المبكر أفضل النتائج في منعها من التحول إلى تجاعيد أعمق."
                },
                {
                    "question": "هل هناك فترة نقاهة بعد العلاج؟",
                    "answer": "معظم علاجات الخطوط الدقيقة تتطلب فترة نقاهة قصيرة أو لا تتطلب أي نقاهة. قد تعاني من احمرار أو تورم خفيف يختفي عادة خلال ساعات إلى يومين."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    }
}
